/**
 * Literafan Notification Service
 * Gerencia notificações do navegador (Web Notifications API) e notificações in-app
 * para inscritos na newsletter sobre novas publicações, artigos e fanarts.
 */

import { subscribeToWebPush } from './webPushService';
import { addNotificationToHistory } from './notificationHistoryService';

export interface PublicationNotificationData {
  id?: string;
  title: string;
  body: string;
  url?: string;
  imageUrl?: string;
  tag?: string;
  type?: 'article' | 'post' | 'welcome' | 'system';
}

const NOTIFICATIONS_ENABLED_KEY = 'literafan_notifications_enabled';
const LAST_NOTIFIED_ARTICLE_KEY = 'literafan_last_notified_article';

export const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

export const areNotificationsEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem(NOTIFICATIONS_ENABLED_KEY);
  if (stored === 'true') return true;
  return isNotificationSupported() && Notification.permission === 'granted';
};

/**
 * Toca um som suave de notificação usando a Web Audio API (sem dependências externas)
 */
export const playNotificationChime = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    // Primeiro tom suave (D5 - 587Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
    gain1.gain.setValueAtTime(0.08, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start();
    osc1.stop(ctx.currentTime + 0.35);

    // Segundo tom harmônico (A5 - 880Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
    gain2.gain.setValueAtTime(0.09, ctx.currentTime + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.1);
    osc2.stop(ctx.currentTime + 0.6);
  } catch (_) {
    // Falha silenciosa em navegadores que bloqueiam áudio sem interação prévia
  }
};

/**
 * Solicita permissão do navegador para enviar notificações e registra a preferência
 */
export const requestAndEnableNotifications = async (): Promise<{
  granted: boolean;
  status: string;
}> => {
  if (typeof window === 'undefined') {
    return { granted: false, status: 'unsupported' };
  }

  // Sempre marca como ativado localmente para o sistema in-app do Literafan
  localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, 'true');

  if (!isNotificationSupported()) {
    // Fallback: Dispara notificação in-app de boas-vindas
    dispatchInAppNotification({
      title: 'Notificações Ativadas! 🔔',
      body: 'Você receberá avisos em primeira mão sobre novas publicações e artigos.',
      type: 'welcome'
    });
    return { granted: true, status: 'in_app_only' };
  }

  try {
    let perm = Notification.permission;
    if (perm === 'default') {
      perm = await Notification.requestPermission();
    }

    if (perm === 'granted') {
      // Registra a inscrição na API de Web Push (para alertas em segundo plano, tela bloqueada e app fechado)
      subscribeToWebPush(['articles', 'chapters', 'all']).catch(err => {
        console.warn('[NotificationService] Falha ao registrar Web Push no background:', err);
      });

      // Envia notificação nativa imediata de confirmação
      sendNativeNotification({
        title: 'Literafan — Notificações Ativadas! 📚',
        body: 'Você agora receberá avisos mesmo com o aplicativo fechado ou em segundo plano.',
        tag: 'literafan-welcome',
        url: '/artigos'
      });

      // Também emite alerta in-app
      dispatchInAppNotification({
        title: 'Notificações Ativadas com Sucesso! 🔔',
        body: 'Alertas de novos artigos configurados para funcionar no celular e desktop.',
        type: 'welcome'
      });

      return { granted: true, status: 'granted' };
    }
 else {
      // Se negado no navegador, ainda ativamos o canal in-app interno
      dispatchInAppNotification({
        title: 'Notificações no Literafan Ativadas! 🔔',
        body: 'Você verá alertas visuais no site sempre que houver novos artigos.',
        type: 'welcome'
      });
      return { granted: true, status: 'in_app_fallback' };
    }
  } catch (err) {
    console.warn('Erro ao solicitar permissão de notificações:', err);
    dispatchInAppNotification({
      title: 'Notificações Literafan Ativadas! 🔔',
      body: 'Você receberá alertas de novas publicações.',
      type: 'welcome'
    });
    return { granted: true, status: 'error_fallback' };
  }
};

/**
 * Dispara notificação nativa do sistema operacional / navegador
 */
export const sendNativeNotification = (data: PublicationNotificationData) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return false;
  }

  try {
    const icon = data.imageUrl || 'https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png';
    const notif = new Notification(data.title, {
      body: data.body,
      icon: icon,
      badge: 'https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png',
      tag: data.tag || data.id || 'literafan-pub',
      dir: 'auto',
      lang: 'pt-BR'
    });

    notif.onclick = (e) => {
      e.preventDefault();
      window.focus();
      if (data.url) {
        if (data.url.startsWith('/#')) {
          window.location.hash = data.url.replace('/#', '');
        } else if (data.url.startsWith('#')) {
          window.location.hash = data.url;
        } else {
          window.location.hash = data.url.startsWith('/') ? data.url : `/${data.url}`;
        }
      }
      notif.close();
    };

    return true;
  } catch (err) {
    console.warn('Erro ao exibir notificação nativa:', err);
    return false;
  }
};

/**
 * Dispara evento customizado para o componente de toast in-app
 */
export const dispatchInAppNotification = (data: PublicationNotificationData) => {
  if (typeof window === 'undefined') return;

  playNotificationChime();

  // Salva no histórico de notificações do sino
  try {
    addNotificationToHistory({
      id: data.id,
      title: data.title,
      body: data.body,
      url: data.url || '/artigos',
      imageUrl: data.imageUrl,
      category: data.type === 'welcome' ? 'welcome' : data.type === 'post' ? 'community' : 'article',
      categoryLabel: data.type === 'welcome' ? 'BOAS-VINDAS' : data.type === 'post' ? 'COMUNIDADE' : 'ARTIGO',
      date: 'Agora',
      read: false
    });
  } catch (err) {
    console.warn('Erro ao salvar no histórico:', err);
  }

  const event = new CustomEvent('literafan:publication_notification', {
    detail: {
      ...data,
      timestamp: Date.now()
    }
  });
  window.dispatchEvent(event);
};

/**
 * Dispara notificação de nova publicação completa (Navegador + In-App)
 */
export const notifyNewPublication = (data: PublicationNotificationData) => {
  // 1. Tenta notificação nativa
  sendNativeNotification(data);
  // 2. Sempre dispara notificação in-app para garantir que o usuário veja
  dispatchInAppNotification(data);
};

/**
 * Testa o sistema de notificação imediatamente para o usuário
 */
export const testPublicationNotification = () => {
  const sampleTitles = [
    'O Fascínio dos Clássicos Góticos',
    'A Arte da Construção de Mundos na Fantasia Épica',
    'Nova Fanart em Destaque no Mural da Comunidade',
    'Resenha Exclusiva: As Crônicas de Nárnia Revisitadas'
  ];
  const randomTitle = sampleTitles[Math.floor(Math.random() * sampleTitles.length)];

  notifyNewPublication({
    id: `test_${Date.now()}`,
    title: `Nova Publicação: ${randomTitle} 📖`,
    body: 'Notificação de teste! Como inscrito na newsletter, você sempre receberá avisos como este.',
    url: '/artigos',
    type: 'article'
  });
};

/**
 * Registra o último artigo notificado para evitar repetições
 */
export const setLastNotifiedArticleId = (id: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LAST_NOTIFIED_ARTICLE_KEY, id);
  }
};

export const getLastNotifiedArticleId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(LAST_NOTIFIED_ARTICLE_KEY);
};
